import { useEffect, useState } from "react";
import Layout from "@/components/Layout";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import {
  Building2, Plus, Users, Trash2, Loader2, X, Search, MoreVertical, PauseCircle, PlayCircle,
} from "lucide-react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";

const STATUS_STYLE = {
  active: "bg-green-50 text-green-600 border-green-200",
  paused: "bg-amber-50 text-amber-600 border-amber-200",
  suspended: "bg-amber-50 text-amber-600 border-amber-200",
  disabled: "bg-slate-100 text-slate-500 border-slate-200",
};

export default function Clients() {
  const [clients, setClients] = useState(null);
  const [query, setQuery] = useState("");
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [toDelete, setToDelete] = useState(null);
  const [form, setForm] = useState({
    name: "", description: "", plan: "standard", retention_days: 90,
    admin_name: "", admin_email: "", admin_password: "",
  });

  const load = () => api.get("/clients").then(({ data }) => setClients(data)).catch(() => setClients([]));
  useEffect(() => { load(); }, []);

  const create = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = {
        name: form.name,
        description: form.description,
        plan: form.plan,
        retention_days: Number(form.retention_days),
      };
      if (form.admin_email && form.admin_password) {
        payload.admin_name = form.admin_name;
        payload.admin_email = form.admin_email;
        payload.admin_password = form.admin_password;
      }
      await api.post("/clients", payload);
      toast.success("Client created");
      setOpen(false);
      setForm({ name: "", description: "", plan: "standard", retention_days: 90, admin_name: "", admin_email: "", admin_password: "" });
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const remove = async () => {
    try {
      await api.delete(`/clients/${toDelete.id}`);
      toast.success("Client deleted");
      setToDelete(null);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const toggleSuspend = async (c) => {
    const status = c.status === "active" ? "suspended" : "active";
    try {
      await api.put(`/clients/${c.id}`, { status });
      toast.success(status === "active" ? `${c.name} reactivated` : `${c.name} suspended — its team can't sign in and monitoring stops`);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const filtered = (clients || []).filter((c) => c.name.toLowerCase().includes(query.toLowerCase()));

  return (
    <Layout>
      <div className="animate-fade-in-up space-y-6">
        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
          <div>
            <p className="text-sm text-slate-400 font-medium">Bloom Dev Labs</p>
            <h1 className="font-heading text-3xl sm:text-4xl font-medium text-slate-900 tracking-tight">Clients</h1>
          </div>
          <button
            onClick={() => setOpen(true)}
            data-testid="add-client-btn"
            className="inline-flex items-center gap-2 px-4 py-2.5 rounded-lg bg-indigo-600 hover:bg-indigo-700 text-indigo-50 font-semibold text-sm transition"
          >
            <Plus className="w-4 h-4" /> New client
          </button>
        </div>

        <div className="relative max-w-sm">
          <Search className="w-4 h-4 text-slate-400 absolute left-3.5 top-1/2 -translate-y-1/2" />
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search clients..."
            data-testid="client-search"
            className="w-full pl-10 pr-4 py-2.5 rounded-lg border border-slate-200 bg-card text-sm placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-brass-500/40"
          />
        </div>

        {clients === null ? (
          <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 text-indigo-600 animate-spin" /></div>
        ) : filtered.length === 0 ? (
          <div className="bg-card rounded-2xl border border-slate-100 p-12 text-center">
            <Building2 className="w-10 h-10 text-slate-300 mx-auto mb-3" />
            <p className="text-sm text-slate-500">No clients found.</p>
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 xl:grid-cols-3 gap-4">
            {filtered.map((c) => (
              <div key={c.id} data-testid="client-card" className="group bg-card rounded-2xl p-5 border border-slate-100 shadow-card hover:shadow-md transition-all">
                <div className="flex items-start justify-between mb-3">
                  <div className="w-11 h-11 rounded-xl bg-indigo-800 text-indigo-50 flex items-center justify-center font-bold">
                    {c.name.slice(0, 2).toUpperCase()}
                  </div>
                  <div className="flex items-center gap-2">
                    <span className={`text-[10px] font-bold uppercase tracking-wide px-2 py-1 rounded-md border ${STATUS_STYLE[c.status] || STATUS_STYLE.active}`}>
                      {c.status}
                    </span>
                    <button
                      onClick={() => toggleSuspend(c)}
                      data-testid={`suspend-client-${c.id}`}
                      title={c.status === "active" ? "Suspend client" : "Reactivate client"}
                      className="p-1.5 rounded-lg text-slate-300 hover:text-amber-600 hover:bg-amber-50 transition opacity-0 group-hover:opacity-100"
                    >
                      {c.status === "active" ? <PauseCircle className="w-4 h-4" /> : <PlayCircle className="w-4 h-4" />}
                    </button>
                    <button
                      onClick={() => setToDelete(c)}
                      data-testid={`delete-client-${c.id}`}
                      className="p-1.5 rounded-lg text-slate-300 hover:text-red-600 hover:bg-red-50 transition opacity-0 group-hover:opacity-100"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
                <h3 className="font-heading font-bold text-slate-900 text-lg leading-tight">{c.name}</h3>
                <p className="text-sm text-slate-500 mt-1 line-clamp-2 min-h-[2.5rem]">{c.description || "No description"}</p>
                <div className="flex items-center justify-between mt-4 pt-4 border-t border-slate-50 text-xs text-slate-500">
                  <span className="flex items-center gap-1.5"><Users className="w-3.5 h-3.5" /> {c.member_count} members</span>
                  <span className="font-mono uppercase text-slate-400">{c.plan} · {c.retention_days}d</span>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Create dialog */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-md max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading">New client</DialogTitle>
          </DialogHeader>
          <form onSubmit={create} className="space-y-4">
            <Field label="Client name" required>
              <input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="client-name-input" className="inp" placeholder="e.g. Mayor Cruz Office" />
            </Field>
            <Field label="Description">
              <textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="client-desc-input" className="inp resize-none" rows={2} placeholder="Short description" />
            </Field>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Plan">
                <select value={form.plan} onChange={(e) => setForm({ ...form, plan: e.target.value })} className="inp">
                  <option value="standard">Standard</option>
                  <option value="pro">Pro</option>
                  <option value="enterprise">Enterprise</option>
                </select>
              </Field>
              <Field label="Retention (days)">
                <select value={form.retention_days} onChange={(e) => setForm({ ...form, retention_days: e.target.value })} className="inp">
                  <option value={30}>30</option>
                  <option value={60}>60</option>
                  <option value={90}>90</option>
                </select>
              </Field>
            </div>
            <div className="border-t border-slate-100 pt-3">
              <p className="text-xs font-semibold text-slate-500 mb-3">Optional: create the first Client Admin</p>
              <div className="space-y-3">
                <input value={form.admin_name} onChange={(e) => setForm({ ...form, admin_name: e.target.value })} data-testid="client-admin-name" className="inp" placeholder="Admin name" />
                <input type="email" value={form.admin_email} onChange={(e) => setForm({ ...form, admin_email: e.target.value })} data-testid="client-admin-email" className="inp" placeholder="admin@client.com" />
                <input type="password" value={form.admin_password} onChange={(e) => setForm({ ...form, admin_password: e.target.value })} data-testid="client-admin-password" className="inp" placeholder="Temporary password" />
              </div>
            </div>
            <DialogFooter>
              <button type="button" onClick={() => setOpen(false)} className="px-4 py-2 rounded-lg bg-slate-100 hover:bg-slate-200 text-slate-700 font-medium text-sm">Cancel</button>
              <button type="submit" disabled={saving} data-testid="submit-client" className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-indigo-600 hover:bg-indigo-700 text-indigo-50 font-semibold text-sm disabled:opacity-60">
                {saving && <Loader2 className="w-4 h-4 animate-spin" />} Create client
              </button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!toDelete} onOpenChange={(o) => !o && setToDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete {toDelete?.name}?</AlertDialogTitle>
            <AlertDialogDescription>
              This permanently deletes the client, its team members, connected accounts and all moderation data. This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={remove} data-testid="confirm-delete-client" className="bg-red-600 hover:bg-red-700">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

    </Layout>
  );
}

function Field({ label, required, children }) {
  return (
    <div>
      <label className="block text-xs font-semibold text-slate-600 mb-1.5">{label}{required && <span className="text-red-500"> *</span>}</label>
      {children}
    </div>
  );
}
